import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addStockPayment,
  getStockList,
  getExpensesByStockList,
  uploadFile,
  deleteStock,
  getProducts,
  getProviders,
  getEstimateUnitEquivalences,
  upsertEstimateUnitEquivalence,
  deleteEstimateUnitEquivalence,
  getStockEstimateConfirmations,
  confirmStockEstimateItem,
  unconfirmStockEstimateItem,
  updateStock,
} from "../api";
import { FiInfo } from "react-icons/fi";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasPermission } from "../utils/permissions";
import { normalizePaginatedResponse } from "../utils/pagination";
import InlineLoading from "../components/InlineLoading";
import { notify } from '../utils/alertBus';
import useConfirmDialog from '../hooks/useConfirmDialog';
import useProductEvents from '../hooks/useProductEvents';
import { applyProductEvent } from '../utils/productEventPatch';

const getTodayDate = () => new Date().toISOString().split("T")[0];
const ESTIMATE_WARNING_THRESHOLD = 5;
const STOCK_METRIC_UNIT_OPTIONS = [
  { value: "gamela", label: "Gamela" },
  { value: "balde", label: "Balde" },
  { value: "granel", label: "Granel" },
  { value: "bin", label: "Bin" },
  { value: "malla", label: "Malla" },
  { value: "cajonMadera", label: "Cajón de madera" },
  { value: "caja", label: "Caja" },
  { value: "saco", label: "Saco" },
  { value: "cajonNegro", label: "Cajón Negro" },
  { value: "cajonBlanco", label: "Cajón Blanco" },
];
const STOCK_PAYMENT_METHODS = [
  { id: "efectivo", label: "Efectivo" },
  { id: "transferencia", label: "Transferencia" },
  { id: "tarjeta", label: "Tarjeta" },
  { id: "cheque", label: "Cheque" },
];

const isBinUnit = (value) => String(value || "").trim().toLowerCase() === "bin";
const isGranelUnit = (value) => String(value || "").trim().toLowerCase() === "granel";
const isKgQuantityUnit = (value) => isBinUnit(value) || isGranelUnit(value);
const normalizeMetricUnit = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const toMetricUnitKey = (value) => {
  const normalized = normalizeMetricUnit(value);
  if (!normalized) return "";
  if (normalized.includes("kg") || normalized.includes("kilo")) return "kg";
  if (normalized.includes("caja")) return "caja";
  if (normalized.includes("cajon")) return "cajon";
  if (normalized.includes("saco")) return "saco";
  if (normalized.includes("malla")) return "malla";
  if (normalized.includes("unidad")) return "unidad";
  if (normalized.includes("balde")) return "balde";
  if (normalized.includes("gamela")) return "gamela";
  if (normalized.includes("plancha")) return "plancha";
  if (normalized.includes("bandeja")) return "bandeja";
  if (normalized.includes("bin")) return "bin";
  if (normalized.includes("granel")) return "granel";
  return normalized;
};

const pickCheapestEntry = (entries) => {
  if (!Array.isArray(entries) || !entries.length) return null;
  return [...entries].sort((a, b) => Number(a.price || 0) - Number(b.price || 0))[0] || null;
};

const resolveCompatibleWholesaleEntry = (entries, stockMetricUnit, equivalenceMap) => {
  if (!Array.isArray(entries) || !entries.length) return null;
  const stockUnitKey = toMetricUnitKey(stockMetricUnit);
  const equivalenceCandidates = stockUnitKey
    ? (equivalenceMap?.get(stockUnitKey) || [])
    : [];

  if (equivalenceCandidates.length) {
    let bestCandidate = null;
    equivalenceCandidates.forEach((equivalence) => {
      const targetEntries = entries.filter(
        (entry) => toMetricUnitKey(entry?.metricUnit) === equivalence.targetUnitKey
      );
      const targetEntry = pickCheapestEntry(targetEntries);
      const targetUnitPrice = Number(targetEntry?.price);
      const multiplier = Number(equivalence.multiplier);
      const convertedPrice =
        Number.isFinite(targetUnitPrice) && targetUnitPrice > 0 && Number.isFinite(multiplier) && multiplier > 0
          ? targetUnitPrice * multiplier
          : null;
      if (!targetEntry || !Number.isFinite(convertedPrice) || convertedPrice <= 0) return;
      if (!bestCandidate || convertedPrice < bestCandidate.unitPrice) {
        bestCandidate = {
          entry: targetEntry,
          unitPrice: convertedPrice,
          rawUnitPrice: targetUnitPrice,
          mode: "equivalence",
          metricUnit: stockMetricUnit || equivalence.sourceUnit || "",
          targetMetricUnit: targetEntry.metricUnit || "",
          equivalence,
        };
      }
    });
    if (bestCandidate) {
      return bestCandidate;
    }
  }

  if (!stockUnitKey) {
    const fallback = entries[0] || null;
    if (!fallback) return null;
    return {
      entry: fallback,
      unitPrice: Number(fallback.price || 0),
      rawUnitPrice: Number(fallback.price || 0),
      mode: "fallback_first",
      metricUnit: fallback.metricUnit || "",
    };
  }

  const exactUnitEntries = entries.filter(
    (entry) => toMetricUnitKey(entry?.metricUnit) === stockUnitKey
  );
  if (exactUnitEntries.length) {
    const resolved = pickCheapestEntry(exactUnitEntries);
    return {
      entry: resolved,
      unitPrice: Number(resolved?.price || 0),
      rawUnitPrice: Number(resolved?.price || 0),
      mode: "exact",
      metricUnit: resolved?.metricUnit || "",
    };
  }

  // For bin/granel purchases, fallback to kg wholesale price when available.
  if (stockUnitKey === "bin" || stockUnitKey === "granel") {
    const kgEntries = entries.filter((entry) => toMetricUnitKey(entry?.metricUnit) === "kg");
    if (kgEntries.length) {
      const resolved = pickCheapestEntry(kgEntries);
      return {
        entry: resolved,
        unitPrice: Number(resolved?.price || 0),
        rawUnitPrice: Number(resolved?.price || 0),
        mode: "kg_fallback",
        metricUnit: resolved?.metricUnit || "",
      };
    }
  }

  // Fallback: use first wholesale price for the variety when no metric unit matches.
  const fallback = entries[0] || null;
  if (!fallback) return null;
  return {
    entry: fallback,
    unitPrice: Number(fallback.price || 0),
    rawUnitPrice: Number(fallback.price || 0),
    mode: "fallback_first",
    metricUnit: fallback.metricUnit || "",
  };
};

const getStockBultos = (stock) => {
  const quantity = Number(stock?.quantity || 0);
  if (!Number.isFinite(quantity) || quantity <= 0) return 0;
  if (isBinUnit(stock?.metricUnit)) {
    const bultosPerBin = Number(stock?.bultosPerBin || 0);
    if (!Number.isFinite(bultosPerBin) || bultosPerBin <= 0) return 0;
    return quantity * bultosPerBin;
  }
  if (isGranelUnit(stock?.metricUnit)) {
    return 0;
  }
  return quantity;
};

const getStockKg = (stock) => {
  const quantity = Number(stock?.quantity || 0);
  const weight = Number(stock?.weight || 0);
  if (!Number.isFinite(quantity) || quantity <= 0) return 0;
  if (isKgQuantityUnit(stock?.metricUnit)) {
    return quantity;
  }
  if (!Number.isFinite(weight) || weight <= 0) return 0;
  return quantity * weight;
};

const formatKg = (value) =>
  Number(value || 0).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatSignificantNumber = (value, maxFractionDigits = 6) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  return numeric.toLocaleString("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  });
};

const parseDecimalInput = (value) => {
  const normalized = String(value ?? "").trim().replace(",", ".");
  if (!normalized) return null;
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : null;
};

const resolveMetricUnitInputValue = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  const normalized = trimmed.toLowerCase();
  const knownOption = STOCK_METRIC_UNIT_OPTIONS.find((option) => option.value === normalized);
  return knownOption ? knownOption.value : trimmed;
};

const buildMetricUnitOptions = (value) => {
  const resolved = resolveMetricUnitInputValue(value);
  if (!resolved) return STOCK_METRIC_UNIT_OPTIONS;
  const exists = STOCK_METRIC_UNIT_OPTIONS.some((option) => option.value === resolved);
  if (exists) return STOCK_METRIC_UNIT_OPTIONS;
  return [{ value: resolved, label: resolved }, ...STOCK_METRIC_UNIT_OPTIONS];
};

const createStockEditForm = (stock) => ({
  price: stock?.price !== undefined && stock?.price !== null ? String(stock.price) : "",
  quantity: stock?.quantity !== undefined && stock?.quantity !== null ? String(stock.quantity) : "",
  metricUnit: resolveMetricUnitInputValue(stock?.metricUnit),
  weight: stock?.weight !== undefined && stock?.weight !== null ? String(stock.weight) : "",
  bultosPerBin:
    stock?.bultosPerBin !== undefined && stock?.bultosPerBin !== null
      ? String(stock.bultosPerBin)
      : "",
  paymentMethod: stock?.paymentMethod || "efectivo",
  providerId: stock?.providerId ? String(stock.providerId) : "",
});

const StockListViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("productos");
  const [stockItems, setStockItems] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingWholesale, setLoadingWholesale] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [stockList, setStockList] = useState(null);
  const [wholesalePriceMap, setWholesalePriceMap] = useState({});
  const [wholesaleError, setWholesaleError] = useState("");
  const productsCacheRef = useRef([]);
  const [paymentForm, setPaymentForm] = useState(() => ({
    amount: "",
    method: "efectivo",
    reference: "",
    paidAt: getTodayDate(),
    voucherFile: null,
  }));
  const [paymentError, setPaymentError] = useState("");
  const { confirm } = useConfirmDialog();
  const [isSavingPayment, setIsSavingPayment] = useState(false);
  const [isDeletingStock, setIsDeletingStock] = useState(false);
  const [deletingStockId, setDeletingStockId] = useState(null);
  const [isEditingStock, setIsEditingStock] = useState(false);
  const [stockEditForm, setStockEditForm] = useState(() => createStockEditForm(null));
  const [stockEditError, setStockEditError] = useState("");
  const [isSavingStockEdit, setIsSavingStockEdit] = useState(false);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [providersLoaded, setProvidersLoaded] = useState(false);
  const [providersError, setProvidersError] = useState("");
  const [editProviders, setEditProviders] = useState([]);
  const [editProviderSearchTerm, setEditProviderSearchTerm] = useState("");
  const [isEditProviderSelectOpen, setIsEditProviderSelectOpen] = useState(false);
  const [estimateEquivalences, setEstimateEquivalences] = useState([]);
  const [confirmedEstimateStockIds, setConfirmedEstimateStockIds] = useState([]);
  const [loadingEstimateEquivalences, setLoadingEstimateEquivalences] = useState(false);
  const [loadingEstimateConfirmations, setLoadingEstimateConfirmations] = useState(false);
  const [estimateReviewError, setEstimateReviewError] = useState("");
  const [estimateActionError, setEstimateActionError] = useState("");
  const [isSavingEstimateConfirmation, setIsSavingEstimateConfirmation] = useState(false);
  const [isSavingEquivalence, setIsSavingEquivalence] = useState(false);
  const [deletingEquivalenceId, setDeletingEquivalenceId] = useState(null);
  const [equivalenceModal, setEquivalenceModal] = useState({
    isOpen: false,
    stockId: null,
    fromUnit: "",
    toUnit: "",
    fromAmount: "1",
    toAmount: "",
    error: "",
  });
  const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
  const { user } = useCurrentUser();
  const canCreateStock = hasPermission(user, "stock.create");
  const canUpdateStock = hasPermission(user, "stock.update");
  const canDeleteStock = hasPermission(user, "stock.delete");
  const editProviderSelectRef = useRef(null);

  const refreshStockListItems = useCallback(async (selectedStockId = null) => {
    const stockListData = await getStockList(branchId, id);
    const nextStocks = stockListData?.Stocks || [];
    setStockList(stockListData);
    setStockItems(nextStocks);
    if (selectedStockId !== null && selectedStockId !== undefined) {
      const refreshedItem = nextStocks.find((item) => Number(item.id) === Number(selectedStockId)) || null;
      setSelectedItem(refreshedItem);
      return refreshedItem;
    }
    return null;
  }, [branchId, id]);

  const loadEstimateEquivalences = useCallback(async () => {
    if (!branchId) return;
    setLoadingEstimateEquivalences(true);
    setEstimateReviewError("");
    try {
      const data = await getEstimateUnitEquivalences(branchId);
      setEstimateEquivalences(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching estimate unit equivalences:", error);
      setEstimateReviewError("No se pudieron cargar las equivalencias de unidades.");
    } finally {
      setLoadingEstimateEquivalences(false);
    }
  }, [branchId]);

  const loadEstimateConfirmations = useCallback(async () => {
    if (!branchId || !id) return;
    setLoadingEstimateConfirmations(true);
    setEstimateReviewError("");
    try {
      const data = await getStockEstimateConfirmations(branchId, id);
      setConfirmedEstimateStockIds(Array.isArray(data?.confirmedStockIds) ? data.confirmedStockIds : []);
    } catch (error) {
      console.error("Error fetching estimate confirmations:", error);
      setEstimateReviewError("No se pudieron cargar las confirmaciones del estimado.");
    } finally {
      setLoadingEstimateConfirmations(false);
    }
  }, [branchId, id]);

  useEffect(() => {
    const fetchStockListData = async () => {
      try {
        await refreshStockListItems();
        const expenseData = await getExpensesByStockList(branchId, id);
        setExpenses(expenseData || []);
      } catch (error) {
        console.error("Error fetching stock list:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStockListData();
  }, [branchId, id, refreshStockListItems]);

  const buildWholesalePriceMap = useCallback((productList) => {
    const priceMap = {};
    (productList || []).forEach((product) => {
      (product.Varieties || []).forEach((variety) => {
        const wholesalePrices = (variety.VarietyPrices || []).filter((price) => {
          const type = String(price.clientType || "").toLowerCase();
          const value = Number(price.price);
          return type === "mayorista" && Number.isFinite(value);
        });
        if (wholesalePrices.length) {
          priceMap[variety.id] = wholesalePrices.map((entry) => ({
            price: Number(entry.price),
            metricUnit: entry.metricUnit || "",
          }));
        }
      });
    });
    return priceMap;
  }, []);

  const fetchWholesalePrices = useCallback(async () => {
    if (!branchId) return;
    setLoadingWholesale(true);
    setWholesaleError("");
    try {
      const data = await getProducts(branchId, { pageSize: 0 });
      const normalized = normalizePaginatedResponse(data);
      const productsData = normalized.data || [];
      productsCacheRef.current = productsData;
      setWholesalePriceMap(buildWholesalePriceMap(productsData));
    } catch (error) {
      console.error("Error fetching wholesale prices:", error);
      setWholesaleError("No se pudo cargar precios mayoristas.");
    } finally {
      setLoadingWholesale(false);
    }
  }, [branchId, buildWholesalePriceMap]);

  const handleProductEvent = useCallback((event) => {
    const currentProducts = productsCacheRef.current || [];
    const nextProducts = applyProductEvent(currentProducts, event);
    if (!nextProducts) {
      fetchWholesalePrices();
      return;
    }
    if (nextProducts !== currentProducts) {
      productsCacheRef.current = nextProducts;
    }
    setWholesalePriceMap(buildWholesalePriceMap(nextProducts));
  }, [fetchWholesalePrices, buildWholesalePriceMap]);

  useProductEvents(branchId, handleProductEvent);

  useEffect(() => {
    fetchWholesalePrices();
  }, [fetchWholesalePrices]);

  useEffect(() => {
    loadEstimateEquivalences();
  }, [loadEstimateEquivalences]);

  useEffect(() => {
    loadEstimateConfirmations();
  }, [loadEstimateConfirmations]);


  const paymentMethods = STOCK_PAYMENT_METHODS;

  const loadEditProviders = useCallback(async () => {
    if (providersLoaded || providersLoading) return;
    setProvidersLoading(true);
    setProvidersError("");
    try {
      const data = await getProviders({ includeDeleted: true, pageSize: 0 });
      const normalized = normalizePaginatedResponse(data);
      setEditProviders(Array.isArray(normalized.data) ? normalized.data : []);
      setProvidersLoaded(true);
    } catch (error) {
      console.error("Error fetching providers for stock editing:", error);
      setProvidersError("No se pudieron cargar los proveedores.");
    } finally {
      setProvidersLoading(false);
    }
  }, [providersLoaded, providersLoading]);

  const estimateEquivalenceMap = useMemo(() => {
    const map = new Map();
    (estimateEquivalences || []).forEach((entry) => {
      const fromUnitKey = toMetricUnitKey(entry?.fromUnitKey || entry?.fromUnit);
      const toUnitKey = toMetricUnitKey(entry?.toUnitKey || entry?.toUnit);
      const fromAmountRaw = Number(entry?.fromAmount);
      const toAmountRaw = Number(entry?.toAmount);
      const factorRaw = Number(entry?.factor);
      const fromAmount =
        Number.isFinite(fromAmountRaw) && fromAmountRaw > 0
          ? fromAmountRaw
          : 1;
      const toAmount =
        Number.isFinite(toAmountRaw) && toAmountRaw > 0
          ? toAmountRaw
          : (Number.isFinite(factorRaw) && factorRaw > 0 ? factorRaw : null);
      if (!fromUnitKey || !toUnitKey) return;
      if (!Number.isFinite(fromAmount) || fromAmount <= 0) return;
      if (!Number.isFinite(toAmount) || toAmount <= 0) return;
      const forwardMultiplier = toAmount / fromAmount;
      const reverseMultiplier = fromAmount / toAmount;
      if (!Number.isFinite(forwardMultiplier) || forwardMultiplier <= 0) return;
      if (!Number.isFinite(reverseMultiplier) || reverseMultiplier <= 0) return;

      const fromLabel = String(entry?.fromUnit || entry?.fromUnitKey || fromUnitKey).trim() || fromUnitKey;
      const toLabel = String(entry?.toUnit || entry?.toUnitKey || toUnitKey).trim() || toUnitKey;

      const forwardList = map.get(fromUnitKey) || [];
      forwardList.push({
        ...entry,
        sourceUnitKey: fromUnitKey,
        sourceUnit: fromLabel,
        sourceAmount: fromAmount,
        targetUnitKey: toUnitKey,
        targetUnit: toLabel,
        targetAmount: toAmount,
        multiplier: forwardMultiplier,
        fromUnitKey,
        toUnitKey,
        fromAmount,
        toAmount,
        direction: "forward",
      });
      map.set(fromUnitKey, forwardList);

      const reverseList = map.get(toUnitKey) || [];
      reverseList.push({
        ...entry,
        sourceUnitKey: toUnitKey,
        sourceUnit: toLabel,
        sourceAmount: toAmount,
        targetUnitKey: fromUnitKey,
        targetUnit: fromLabel,
        targetAmount: fromAmount,
        multiplier: reverseMultiplier,
        fromUnitKey,
        toUnitKey,
        fromAmount,
        toAmount,
        direction: "reverse",
      });
      map.set(toUnitKey, reverseList);
    });
    return map;
  }, [estimateEquivalences]);

  const confirmedEstimateSet = useMemo(
    () => new Set((confirmedEstimateStockIds || []).map((value) => Number(value)).filter((value) => Number.isFinite(value))),
    [confirmedEstimateStockIds]
  );

  const resolveWholesaleUnitOptions = useCallback((item) => {
    const entries = wholesalePriceMap[item?.varietyId] || [];
    const sourceKey = toMetricUnitKey(item?.metricUnit);
    const options = [];
    const seen = new Set();
    entries.forEach((entry) => {
      const unitLabel = String(entry?.metricUnit || "").trim();
      const unitKey = toMetricUnitKey(unitLabel);
      if (!unitLabel || !unitKey) return;
      if (unitKey === sourceKey) return;
      if (seen.has(unitKey)) return;
      seen.add(unitKey);
      options.push({ key: unitKey, label: unitLabel });
    });
    return options;
  }, [wholesalePriceMap]);

  const getPaymentSummary = (stock) => {
    const totalAmount = Number(stock.price || 0) * Number(stock.quantity || 0);
    const paidAmount = (stock.StockPayments || []).reduce(
      (sum, payment) => sum + Number(payment.amount || 0),
      0
    );
    const balance = Math.max(totalAmount - paidAmount, 0);
    const paymentStatus =
      paidAmount <= 0 ? "unpaid" : paidAmount < totalAmount ? "partial" : "paid";
    return { totalAmount, paidAmount, balance, paymentStatus };
  };

  const resetPaymentForm = () => {
    setPaymentForm({ amount: "", method: "efectivo", reference: "", paidAt: getTodayDate(), voucherFile: null });
    setPaymentError("");
  };

  useEffect(() => {
    if (selectedItem && activeTab === "productos") {
      resetPaymentForm();
    }
  }, [selectedItem, activeTab]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        editProviderSelectRef.current &&
        !editProviderSelectRef.current.contains(event.target)
      ) {
        setIsEditProviderSelectOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePaymentChange = (field, value) => {
    setPaymentForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePaymentVoucherChange = (file) => {
    setPaymentForm((prev) => ({ ...prev, voucherFile: file || null }));
  };

  const handleAddPayment = async () => {
    if (!canUpdateStock) return;
    if (!selectedItem) return;
    setPaymentError("");
    const amount = Number(paymentForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setPaymentError("Monto invalido.");
      return;
    }
    if (!paymentForm.method) {
      setPaymentError("Selecciona un metodo de pago.");
      return;
    }
    const summary = getPaymentSummary(selectedItem);
    if (amount > summary.balance) {
      setPaymentError("El monto supera lo pendiente.");
      return;
    }
    setIsSavingPayment(true);
    try {
      let voucherUrl;
      if (paymentForm.voucherFile) {
        const response = await uploadFile(paymentForm.voucherFile);
        voucherUrl = response?.url;
      }
      const payload = {
        amount,
        method: paymentForm.method,
        reference: paymentForm.reference || undefined,
        paidAt: paymentForm.paidAt || undefined,
      };
      if (voucherUrl) {
        payload.voucherUrl = voucherUrl;
      }
      const created = await addStockPayment(branchId, selectedItem.id, payload);
      const updatedPayments = [...(selectedItem.StockPayments || []), created];
      const summary = getPaymentSummary({ ...selectedItem, StockPayments: updatedPayments });
      const updatedItem = {
        ...selectedItem,
        StockPayments: updatedPayments,
        paidAmount: summary.paidAmount,
        balance: summary.balance,
        paymentStatus: summary.paymentStatus,
      };
      setSelectedItem(updatedItem);
      setStockItems((prev) =>
        prev.map((item) => (item.id === selectedItem.id ? updatedItem : item))
      );
      resetPaymentForm();
    } catch (error) {
      console.error(error);
      setPaymentError("Error al registrar pago.");
    } finally {
      setIsSavingPayment(false);
    }
  };

  const handleCloseModal = () => {
    handleCloseEquivalenceModal();
    setEstimateActionError("");
    setIsEditingStock(false);
    setStockEditForm(createStockEditForm(null));
    setStockEditError("");
    setEditProviderSearchTerm("");
    setIsEditProviderSelectOpen(false);
    setSelectedItem(null);
    resetPaymentForm();
  };

  const handleStartEditingStock = () => {
    if (!canUpdateStock || !selectedItem || activeTab !== "productos") return;
    setIsEditingStock(true);
    setStockEditForm(createStockEditForm(selectedItem));
    setStockEditError("");
    setEditProviderSearchTerm("");
    setIsEditProviderSelectOpen(false);
    loadEditProviders();
  };

  const handleCancelEditingStock = () => {
    setIsEditingStock(false);
    setStockEditForm(createStockEditForm(selectedItem));
    setStockEditError("");
    setEditProviderSearchTerm("");
    setIsEditProviderSelectOpen(false);
  };

  const handleStockEditChange = (field, value) => {
    setStockEditError("");
    setStockEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleStockEditMetricUnitChange = (value) => {
    setStockEditError("");
    setStockEditForm((prev) => ({
      ...prev,
      metricUnit: value,
      bultosPerBin: isBinUnit(value) ? prev.bultosPerBin : "",
    }));
  };

  const handleSelectEditProvider = (providerId) => {
    setStockEditError("");
    setStockEditForm((prev) => ({
      ...prev,
      providerId: providerId ? String(providerId) : "",
    }));
    setEditProviderSearchTerm("");
    setIsEditProviderSelectOpen(false);
  };

  const handleSaveStockEdit = async () => {
    if (!canUpdateStock || !selectedItem || activeTab !== "productos" || isSavingStockEdit) return;

    const price = Number(stockEditForm.price);
    if (!Number.isFinite(price) || price <= 0) {
      setStockEditError("Ingresa un precio válido mayor a 0.");
      return;
    }

    const quantity = Number(stockEditForm.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setStockEditError("Ingresa una cantidad válida mayor a 0.");
      return;
    }

    const metricUnit = String(stockEditForm.metricUnit || "").trim();
    if (!metricUnit) {
      setStockEditError("Selecciona una unidad métrica.");
      return;
    }

    if (!stockEditForm.providerId) {
      setStockEditError("Selecciona un proveedor.");
      return;
    }

    if (!stockEditForm.paymentMethod) {
      setStockEditError("Selecciona un método de pago.");
      return;
    }

    const weightValue = String(stockEditForm.weight || "").trim();
    const parsedWeight = weightValue === "" ? null : Number(weightValue);
    if (weightValue !== "" && !Number.isFinite(parsedWeight)) {
      setStockEditError("Ingresa un peso válido.");
      return;
    }

    let parsedBultosPerBin = null;
    if (isBinUnit(metricUnit)) {
      parsedBultosPerBin = Number(stockEditForm.bultosPerBin);
      if (!Number.isInteger(parsedBultosPerBin) || parsedBultosPerBin <= 0) {
        setStockEditError("Ingresa los bultos por bin.");
        return;
      }
    }

    setIsSavingStockEdit(true);
    setStockEditError("");
    try {
      await updateStock(branchId, selectedItem.id, {
        price,
        quantity,
        metricUnit,
        weight: parsedWeight,
        bultosPerBin: isBinUnit(metricUnit) ? parsedBultosPerBin : null,
        paymentMethod: stockEditForm.paymentMethod,
        providerId: Number(stockEditForm.providerId),
      });
      const refreshedItem = await refreshStockListItems(selectedItem.id);
      setIsEditingStock(false);
      setStockEditForm(createStockEditForm(refreshedItem));
      setEditProviderSearchTerm("");
      setIsEditProviderSelectOpen(false);
      notify("Compra actualizada.");
    } catch (error) {
      console.error("Error updating stock item:", error);
      setStockEditError(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "No se pudo actualizar la compra."
      );
    } finally {
      setIsSavingStockEdit(false);
    }
  };

  const handleDeleteStockItem = async () => {
    if (!canDeleteStock) return;
    if (!selectedItem) return;
    const label = selectedItem.varietyName || "producto";
    const approved = await confirm({
      title: 'Eliminar compra',
      message: `Borrar compra de ${label}?`,
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    setIsDeletingStock(true);
    try {
      await deleteStock(branchId, selectedItem.id);
      setStockItems((prev) => prev.filter((item) => item.id !== selectedItem.id));
      setConfirmedEstimateStockIds((prev) =>
        prev.filter((entryId) => Number(entryId) !== Number(selectedItem.id))
      );
      if (equivalenceModal.stockId && Number(equivalenceModal.stockId) === Number(selectedItem.id)) {
        handleCloseEquivalenceModal();
      }
      setSelectedItem(null);
    } catch (error) {
      console.error("Error deleting stock item:", error);
      notify("Error al eliminar la compra.");
    } finally {
      setIsDeletingStock(false);
    }
  };

  const handleDeleteStockFromList = async (event, stock) => {
    event.stopPropagation();
    if (!canDeleteStock) return;
    if (!stock) return;
    const label = stock.varietyName || "producto";
    const approved = await confirm({
      title: 'Eliminar compra',
      message: `Borrar compra de ${label}?`,
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    setDeletingStockId(stock.id);
    try {
      await deleteStock(branchId, stock.id);
      setStockItems((prev) => prev.filter((item) => item.id !== stock.id));
      setConfirmedEstimateStockIds((prev) =>
        prev.filter((entryId) => Number(entryId) !== Number(stock.id))
      );
      if (equivalenceModal.stockId && Number(equivalenceModal.stockId) === Number(stock.id)) {
        handleCloseEquivalenceModal();
      }
      if (selectedItem?.id === stock.id) {
        setSelectedItem(null);
      }
    } catch (error) {
      console.error("Error deleting stock item:", error);
      notify("Error al eliminar la compra.");
    } finally {
      setDeletingStockId(null);
    }
  };

  const handleConfirmSelectedEstimate = async () => {
    if (!canUpdateStock || !selectedItem?.id || !id) return;
    setEstimateActionError("");
    setIsSavingEstimateConfirmation(true);
    try {
      await confirmStockEstimateItem(branchId, id, selectedItem.id);
      setConfirmedEstimateStockIds((prev) => Array.from(new Set([...prev, selectedItem.id])));
    } catch (error) {
      console.error("Error confirming estimate item:", error);
      setEstimateActionError("No se pudo confirmar este item del estimado.");
    } finally {
      setIsSavingEstimateConfirmation(false);
    }
  };

  const handleUnconfirmSelectedEstimate = async () => {
    if (!canUpdateStock || !selectedItem?.id || !id) return;
    setEstimateActionError("");
    setIsSavingEstimateConfirmation(true);
    try {
      await unconfirmStockEstimateItem(branchId, id, selectedItem.id);
      setConfirmedEstimateStockIds((prev) =>
        prev.filter((entryId) => Number(entryId) !== Number(selectedItem.id))
      );
    } catch (error) {
      console.error("Error removing estimate confirmation:", error);
      setEstimateActionError("No se pudo quitar la confirmación del item.");
    } finally {
      setIsSavingEstimateConfirmation(false);
    }
  };

  const handleOpenEquivalenceModal = () => {
    if (!canUpdateStock || !selectedItem?.id) return;
    const options = resolveWholesaleUnitOptions(selectedItem);
    if (!options.length) {
      setEstimateActionError("No hay unidades mayoristas disponibles para crear equivalencia.");
      return;
    }
    setEquivalenceModal({
      isOpen: true,
      stockId: selectedItem.id,
      fromUnit: selectedItem.metricUnit || "",
      toUnit: options[0].label,
      fromAmount: "1",
      toAmount: "",
      error: "",
    });
  };

  const handleCloseEquivalenceModal = () => {
    setEquivalenceModal({
      isOpen: false,
      stockId: null,
      fromUnit: "",
      toUnit: "",
      fromAmount: "1",
      toAmount: "",
      error: "",
    });
  };

  const handleSaveEquivalence = async () => {
    if (!canUpdateStock || !selectedItem?.id) return;
    const fromUnit = String(equivalenceModal.fromUnit || "").trim();
    const toUnit = String(equivalenceModal.toUnit || "").trim();
    const fromAmount = parseDecimalInput(equivalenceModal.fromAmount);
    const toAmount = parseDecimalInput(equivalenceModal.toAmount);
    if (!fromUnit || !toUnit) {
      setEquivalenceModal((prev) => ({ ...prev, error: "Selecciona unidades válidas." }));
      return;
    }
    if (!Number.isFinite(fromAmount) || fromAmount <= 0 || !Number.isFinite(toAmount) || toAmount <= 0) {
      setEquivalenceModal((prev) => ({ ...prev, error: "Ingresa cantidades mayores que 0 para ambas unidades." }));
      return;
    }

    setEstimateActionError("");
    setIsSavingEquivalence(true);
    try {
      await upsertEstimateUnitEquivalence(branchId, { fromUnit, fromAmount, toUnit, toAmount });
      await loadEstimateEquivalences();
      handleCloseEquivalenceModal();
    } catch (error) {
      console.error("Error saving estimate equivalence:", error);
      setEquivalenceModal((prev) => ({
        ...prev,
        error: "No se pudo guardar la equivalencia.",
      }));
    } finally {
      setIsSavingEquivalence(false);
    }
  };

  const handleDeleteEquivalence = async (equivalenceId) => {
    if (!canUpdateStock || !equivalenceId) return;
    setEstimateActionError("");
    setDeletingEquivalenceId(equivalenceId);
    try {
      await deleteEstimateUnitEquivalence(branchId, equivalenceId);
      await loadEstimateEquivalences();
    } catch (error) {
      console.error("Error deleting estimate equivalence:", error);
      setEquivalenceModal((prev) => ({
        ...prev,
        error: "No se pudo eliminar la equivalencia.",
      }));
    } finally {
      setDeletingEquivalenceId(null);
    }
  };

  // Calculate total stock cost and expenses
  const totalStockValue = stockItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const overallTotal = totalStockValue + totalExpenses;
  const totalBultos =
    stockList?.stockTotals?.totalBultos ??
    stockItems.reduce((sum, item) => sum + getStockBultos(item), 0);
  const totalKg =
    stockList?.stockTotals?.totalKg ??
    stockItems.reduce((sum, item) => sum + getStockKg(item), 0);
  const resolveEstimateMetaForItem = (item) => {
    const quantity = Number(item?.quantity || 0);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      return { considered: false, reason: "Cantidad inválida o no informada." };
    }
    const wholesaleEntries = wholesalePriceMap[item?.varietyId] || [];
    const resolvedSelection = resolveCompatibleWholesaleEntry(
      wholesaleEntries,
      item?.metricUnit,
      estimateEquivalenceMap
    );
    if (!resolvedSelection) {
      return { considered: false, reason: "Sin precio mayorista disponible para esta variedad." };
    }
    const unitPrice = Number(resolvedSelection.unitPrice);
    if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
      return { considered: false, reason: "Precio mayorista inválido para el cálculo." };
    }
    const estimate = unitPrice * quantity;
    return {
      considered: true,
      estimate,
      unitPrice,
      rawUnitPrice: Number(resolvedSelection.rawUnitPrice || unitPrice),
      metricUnit: resolvedSelection.metricUnit || "",
      wholesaleMetricUnit: resolvedSelection.targetMetricUnit || resolvedSelection.entry?.metricUnit || "",
      quantity,
      mode: resolvedSelection.mode || "exact",
      usedEquivalence: resolvedSelection.mode === "equivalence",
      equivalence: resolvedSelection.equivalence || null,
    };
  };
  const estimateMetaByStockId = useMemo(() => {
    const map = new Map();
    (stockItems || []).forEach((item) => {
      map.set(Number(item.id), resolveEstimateMetaForItem(item));
    });
    return map;
  }, [stockItems, wholesalePriceMap, estimateEquivalenceMap]);

  const wholesaleEstimate = useMemo(
    () => stockItems.reduce((sum, item) => {
      const estimateMeta = estimateMetaByStockId.get(Number(item.id));
      return sum + (estimateMeta?.considered ? Number(estimateMeta.estimate || 0) : 0);
    }, 0),
    [stockItems, estimateMetaByStockId]
  );

  const missingWholesaleCount = useMemo(
    () => stockItems.filter((item) => !estimateMetaByStockId.get(Number(item.id))?.considered).length,
    [stockItems, estimateMetaByStockId]
  );

  const estimateReviewByStockId = useMemo(() => {
    const map = new Map();
    (stockItems || []).forEach((item) => {
      const stockId = Number(item.id);
      const meta = estimateMetaByStockId.get(stockId);
      if (!meta?.considered || wholesaleEstimate <= 0) {
        map.set(stockId, {
          sharePercent: 0,
          isConfirmed: confirmedEstimateSet.has(stockId),
          needsReview: false,
        });
        return;
      }
      const sharePercent = (Number(meta.estimate || 0) / wholesaleEstimate) * 100;
      const isConfirmed = confirmedEstimateSet.has(stockId);
      const needsReview =
        sharePercent > ESTIMATE_WARNING_THRESHOLD
        && !isConfirmed
        && !meta.usedEquivalence;
      map.set(stockId, { sharePercent, isConfirmed, needsReview });
    });
    return map;
  }, [stockItems, estimateMetaByStockId, wholesaleEstimate, confirmedEstimateSet]);

  const warningEstimateCount = useMemo(
    () => stockItems.filter((item) => estimateReviewByStockId.get(Number(item.id))?.needsReview).length,
    [stockItems, estimateReviewByStockId]
  );

  const hasMissingBultosInfo = stockItems.some((item) => {
    const unit = item?.metricUnit;
    if (!unit) return true;
    if (isBinUnit(unit)) {
      const bultosPerBin = Number(item?.bultosPerBin || 0);
      return !Number.isFinite(bultosPerBin) || bultosPerBin <= 0;
    }
    return false;
  });
  const paymentStatusLabel = {
    unpaid: "Pendiente",
    partial: "Parcial",
    paid: "Pagado",
  };
  const paymentStatusClass = {
    unpaid: "bg-red-100 text-red-600",
    partial: "bg-yellow-100 text-yellow-700",
    paid: "bg-green-100 text-green-600",
  };
  const selectedEditProviderId = String(stockEditForm.providerId || "");
  const normalizedEditProviderSearch = editProviderSearchTerm.trim().toLowerCase();
  const availableEditProviders = useMemo(
    () => editProviders.filter((provider) => !provider.isDeleted),
    [editProviders]
  );
  const filteredEditProviders = useMemo(
    () =>
      availableEditProviders.filter((provider) => {
        const providerId = String(provider.id);
        if (providerId === selectedEditProviderId) return true;
        if (!normalizedEditProviderSearch) return true;
        const name = provider.name?.toLowerCase() || "";
        const rut = provider.rut?.toLowerCase() || "";
        const giro = provider.giro?.toLowerCase() || "";
        return (
          name.includes(normalizedEditProviderSearch) ||
          rut.includes(normalizedEditProviderSearch) ||
          giro.includes(normalizedEditProviderSearch)
        );
      }),
    [availableEditProviders, normalizedEditProviderSearch, selectedEditProviderId]
  );
  const selectedEditProvider = useMemo(() => {
    if (!selectedEditProviderId) return null;
    return (
      editProviders.find((provider) => String(provider.id) === selectedEditProviderId) ||
      (selectedItem?.Provider && String(selectedItem.Provider.id) === selectedEditProviderId
        ? selectedItem.Provider
        : null)
    );
  }, [editProviders, selectedEditProviderId, selectedItem]);
  const stockMetricUnitOptions = useMemo(
    () => buildMetricUnitOptions(stockEditForm.metricUnit || selectedItem?.metricUnit),
    [stockEditForm.metricUnit, selectedItem?.metricUnit]
  );
  const previewPrice = Number(stockEditForm.price);
  const previewQuantity = Number(stockEditForm.quantity);
  const selectedSummary =
    selectedItem && activeTab === "productos"
      ? getPaymentSummary({
          ...selectedItem,
          price:
            isEditingStock && Number.isFinite(previewPrice) && previewPrice > 0
              ? previewPrice
              : selectedItem.price,
          quantity:
            isEditingStock && Number.isFinite(previewQuantity) && previewQuantity > 0
              ? previewQuantity
              : selectedItem.quantity,
        })
      : null;
  const selectedBalance = Number(selectedSummary?.balance || 0);
  const enteredAmount = Number(paymentForm.amount || 0);
  const safeEnteredAmount = Number.isFinite(enteredAmount) ? enteredAmount : 0;
  const remainingAfter = Math.max(selectedBalance - safeEnteredAmount, 0);
  const overpayAmount = safeEnteredAmount > selectedBalance ? safeEnteredAmount - selectedBalance : 0;
  const selectedEstimateMeta =
    selectedItem && activeTab === "productos"
      ? estimateMetaByStockId.get(Number(selectedItem.id))
      : null;
  const selectedEstimateReview =
    selectedItem && activeTab === "productos"
      ? estimateReviewByStockId.get(Number(selectedItem.id))
      : null;
  const selectedEstimateValue =
    selectedEstimateMeta?.considered ? Number(selectedEstimateMeta.estimate || 0) : 0;
  const selectedEstimateShare = Number(selectedEstimateReview?.sharePercent || 0);
  const selectedNeedsEstimateReview = Boolean(selectedEstimateReview?.needsReview);
  const selectedEstimateConfirmed = Boolean(selectedEstimateReview?.isConfirmed);
  const selectedStockEquivalenceOptions = selectedItem
    ? resolveWholesaleUnitOptions(selectedItem)
    : [];
  const selectedSourceUnitKey = toMetricUnitKey(selectedItem?.metricUnit);
  const selectedEquivalencesForSource = selectedSourceUnitKey
    ? (estimateEquivalenceMap.get(selectedSourceUnitKey) || [])
    : [];
  const estimateModeLabel = {
    equivalence: "Equivalencia global",
    exact: "Unidad exacta",
    kg_fallback: "Fallback a kg",
    fallback_first: "Primer mayorista",
  };
  const selectedEstimateSourceLabel = selectedEstimateMeta
    ? (estimateModeLabel[selectedEstimateMeta.mode] || "Estimado")
    : "";
  const modalFromAmountValue = parseDecimalInput(equivalenceModal.fromAmount);
  const modalToAmountValue = parseDecimalInput(equivalenceModal.toAmount);
  const modalHasValidRatio =
    Number.isFinite(modalFromAmountValue) &&
    modalFromAmountValue > 0 &&
    Number.isFinite(modalToAmountValue) &&
    modalToAmountValue > 0;
  const modalForwardRatio = modalHasValidRatio ? (modalToAmountValue / modalFromAmountValue) : null;
  const modalReverseRatio = modalHasValidRatio ? (modalFromAmountValue / modalToAmountValue) : null;
  const modalSourceAmountLabel = modalHasValidRatio ? formatSignificantNumber(modalFromAmountValue) : "?";
  const modalTargetAmountLabel = modalHasValidRatio ? formatSignificantNumber(modalToAmountValue) : "?";
  const modalForwardRatioLabel = modalHasValidRatio ? formatSignificantNumber(modalForwardRatio) : "?";
  const modalReverseRatioLabel = modalHasValidRatio ? formatSignificantNumber(modalReverseRatio) : "?";

  if (loading) {
    return (
      <p className="text-center mt-6">
        <InlineLoading />
      </p>
    );
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Page Title */}
        <h1 className="text-2xl font-bold text-center">{stockList.name}</h1>

        {/* Total Info */}
        <div className="text-center text-lg font-semibold text-gray-700 mt-2">
          <p className="text-blue-600 text-xl">Total General: ${overallTotal.toLocaleString("de-DE")}</p>
        </div>
        <div className="text-center text-sm text-emerald-700 mt-1 flex items-center justify-center gap-2">
          <span>
            {loadingWholesale
              ? "Calculando estimado mayorista..."
              : `Estimado venta mayorista: $${wholesaleEstimate.toLocaleString("de-DE")}`}
          </span>
          {!loadingWholesale && (
          <span
              className="inline-flex items-center text-emerald-700"
              title="Prioridad del cálculo: equivalencia global > unidad exacta > fallback kg (bin/granel) > primer precio mayorista."
            >
              <FiInfo className="h-4 w-4" aria-hidden="true" />
            </span>
          )}
        </div>
        {(loadingEstimateEquivalences || loadingEstimateConfirmations) && (
          <p className="text-xs text-gray-500 mt-1 text-center">
            Cargando revisiones del estimado...
          </p>
        )}
        {wholesaleError && (
          <p className="text-xs text-red-600 mt-1 text-center">{wholesaleError}</p>
        )}
        {estimateReviewError && (
          <p className="text-xs text-red-600 mt-1 text-center">{estimateReviewError}</p>
        )}
        {estimateActionError && (
          <p className="text-xs text-red-600 mt-1 text-center">{estimateActionError}</p>
        )}
        {!loadingWholesale && warningEstimateCount > 0 && (
          <p className="text-xs text-amber-700 mt-1 text-center font-semibold">
            {warningEstimateCount} items superan {ESTIMATE_WARNING_THRESHOLD}% del estimado y requieren confirmación o equivalencia.
          </p>
        )}
        {!loadingWholesale && !wholesaleError && missingWholesaleCount > 0 && (
          <p className="text-xs text-gray-500 mt-1 text-center">
            {missingWholesaleCount} items sin precio mayorista considerado.
          </p>
        )}
        <div className="flex flex-wrap justify-center gap-3 mt-3 text-sm text-gray-700">
          <span className="bg-white px-3 py-1 rounded shadow-sm">
            Bultos: {Number(totalBultos || 0).toLocaleString("de-DE")}
            {hasMissingBultosInfo && (
              <span
                className="inline-flex items-center ml-2 text-gray-500"
                title="La cantidad de bultos no esta informada en todos los productos, por lo que el valor es un estimado."
              >
                <FiInfo className="h-4 w-4" aria-hidden="true" />
              </span>
            )}
          </span>
          <span className="bg-white px-3 py-1 rounded shadow-sm">
            Kg: {formatKg(totalKg)}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          Bultos: suma de cantidades (Bin usa bultos por bin; granel no suma). Kg: cantidad * peso; Bin/Granel usan la cantidad.
        </p>

        {/* Tabs */}
        <div className="flex justify-center gap-4 my-6">
          <button
            className={`px-5 py-3 rounded-lg font-semibold ${activeTab === "productos" ? "bg-blue-500 text-white" : "bg-white shadow"
              }`}
            onClick={() => setActiveTab("productos")}
          >
            Productos
          </button>
          <button
            className={`px-5 py-3 rounded-lg font-semibold ${activeTab === "gastos" ? "bg-blue-500 text-white" : "bg-white shadow"
              }`}
            onClick={() => setActiveTab("gastos")}
          >
            Gastos
          </button>
        </div>

        {/* Create New Item Buttons */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">
            {activeTab === "productos" ? "Productos" : "Gastos"}
          </h2>
          {canCreateStock && (
            <button
              onClick={() => navigate(`/branches/${branchId}/stock/${id}/${activeTab === "productos" ? "create" : "create-expense"}`)}
              className="bg-green-500 text-white px-5 py-3 rounded hover:bg-green-600"
            >
              {activeTab === "productos" ? "Añadir Producto" : "Añadir Gasto"}
            </button>
          )}
        </div>
        {activeTab === "productos" ? <p className="font-bold text-center w-full py-4">Total Productos: ${totalStockValue.toLocaleString("de-DE")}</p> : <p className="font-bold text-center w-full py-4">Total Gastos: ${totalExpenses.toLocaleString("de-DE")}</p>}

        {/* Products Tab */}
        {activeTab === "productos" && (
          <div className="bg-white rounded-lg shadow">
            {stockItems.length === 0 ? (
              <p className="text-center text-gray-500 py-6">No hay productos en esta lista.</p>
            ) : (
              <>
                <div className="md:hidden p-3 space-y-3">
                  {stockItems.map((stock) => {
                    const summary = getPaymentSummary(stock);
                    const status = stock.paymentStatus || summary.paymentStatus;
                    const paidAmountValue = stock.paidAmount ?? summary.paidAmount;
                    const balanceValue = stock.balance ?? summary.balance;
                    const unitPrice = Number(stock.price || 0);
                    const qty = Number(stock.quantity || 0);
                    const subtotal = unitPrice * qty;
                    const estimateReview = estimateReviewByStockId.get(Number(stock.id));

                    return (
                      <article key={stock.id} className="p-4 space-y-3 rounded-xl border border-gray-200 shadow-sm">
                        <button
                          type="button"
                          className="w-full text-left"
                          onClick={() => setSelectedItem(stock)}
                        >
                          <div className="flex items-start gap-3">
                            <img
                              src={stock.varietyImage || "https://placehold.co/200"}
                              alt={stock.varietyName || "Producto sin nombre"}
                              className="w-14 h-14 object-cover rounded-lg flex-none"
                            />
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 break-words">
                                {stock.varietyName || "Producto"}
                              </p>
                              {stock.Provider?.name && (
                                <p className="text-xs text-gray-500 break-words">
                                  Proveedor: {stock.Provider.name}
                                </p>
                              )}
                              {estimateReview?.needsReview && (
                                <span className="mt-1 inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-100 text-amber-700">
                                  Revisar estimado
                                </span>
                              )}
                            </div>
                          </div>
                        </button>

                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                          <div className="rounded-lg bg-gray-50 px-2 py-1.5">
                            <p className="text-[11px] text-gray-500">Unidad</p>
                            <p className="font-semibold text-gray-800">{stock.metricUnit || "unidad"}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 px-2 py-1.5">
                            <p className="text-[11px] text-gray-500">Cantidad</p>
                            <p className="font-semibold text-gray-800">{formatSignificantNumber(qty)}</p>
                          </div>
                          <div className="rounded-lg bg-gray-50 px-2 py-1.5">
                            <p className="text-[11px] text-gray-500">Precio unit.</p>
                            <p className="font-semibold text-gray-800">
                              ${unitPrice.toLocaleString("de-DE")}
                            </p>
                          </div>
                          <div className="rounded-lg bg-gray-50 px-2 py-1.5">
                            <p className="text-[11px] text-gray-500">Subtotal</p>
                            <p className="font-semibold text-gray-900">
                              ${subtotal.toLocaleString("de-DE")}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start justify-between gap-3">
                          <div className="text-xs text-gray-600">
                            <p>Pagado: ${Number(paidAmountValue || 0).toLocaleString("de-DE")}</p>
                            <p>Pendiente: ${Number(balanceValue || 0).toLocaleString("de-DE")}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {status && (
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${paymentStatusClass[status] || "bg-gray-100 text-gray-600"}`}
                              >
                                {paymentStatusLabel[status] || "Pendiente"}
                              </span>
                            )}
                            {canDeleteStock && (
                              <button
                                type="button"
                                onClick={(event) => handleDeleteStockFromList(event, stock)}
                                disabled={deletingStockId === stock.id}
                                className="bg-red-500 text-white p-2 rounded hover:bg-red-600 disabled:opacity-60"
                                aria-label="Borrar compra"
                                title="Borrar compra"
                              >
                                {deletingStockId === stock.id ? (
                                  <span className="text-xs">...</span>
                                ) : (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="1.5"
                                    stroke="currentColor"
                                    className="w-4 h-4"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.166L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.021-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                    />
                                  </svg>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>

                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Producto</th>
                        <th className="px-3 py-2 text-left font-semibold">Unidad</th>
                        <th className="px-3 py-2 text-right font-semibold">Cantidad</th>
                        <th className="px-3 py-2 text-right font-semibold">Precio unit.</th>
                        <th className="px-3 py-2 text-right font-semibold">Subtotal</th>
                        <th className="px-3 py-2 text-right font-semibold">Pagos</th>
                        <th className="px-3 py-2 text-right font-semibold">Estado</th>
                        {canDeleteStock && (
                          <th className="px-3 py-2 text-right font-semibold">Acciones</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {stockItems.map((stock) => {
                        const summary = getPaymentSummary(stock);
                        const status = stock.paymentStatus || summary.paymentStatus;
                        const paidAmountValue = stock.paidAmount ?? summary.paidAmount;
                        const balanceValue = stock.balance ?? summary.balance;
                        const unitPrice = Number(stock.price || 0);
                        const qty = Number(stock.quantity || 0);
                        const subtotal = unitPrice * qty;
                        const estimateReview = estimateReviewByStockId.get(Number(stock.id));

                        return (
                          <tr
                            key={stock.id}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => setSelectedItem(stock)}
                          >
                            <td className="px-3 py-3 text-left">
                              <div className="flex items-center gap-3">
                                <img
                                  src={stock.varietyImage || "https://placehold.co/200"}
                                  alt={stock.varietyName || "Producto sin nombre"}
                                  className="w-12 h-12 object-cover rounded"
                                />
                                <div className="flex flex-col min-w-0">
                                  <span className="font-semibold text-gray-900 break-words">
                                    {stock.varietyName || "Producto"}
                                  </span>
                                  {stock.Provider?.name && (
                                    <span className="text-xs text-gray-500 break-words">
                                      Proveedor: {stock.Provider.name}
                                    </span>
                                  )}
                                  {estimateReview?.needsReview && (
                                    <span className="mt-1 inline-flex w-fit px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-100 text-amber-700">
                                      Revisar estimado
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-3 text-left text-gray-600">
                              {stock.metricUnit || "unidad"}
                            </td>
                            <td className="px-3 py-3 text-right text-gray-700">
                              {formatSignificantNumber(qty)}
                            </td>
                            <td className="px-3 py-3 text-right text-gray-700">
                              ${unitPrice.toLocaleString("de-DE")}
                            </td>
                            <td className="px-3 py-3 text-right font-semibold text-gray-900">
                              ${subtotal.toLocaleString("de-DE")}
                            </td>
                            <td className="px-3 py-3 text-right text-xs text-gray-600">
                              <div className="flex flex-col items-end">
                                <span>Pagado: ${Number(paidAmountValue || 0).toLocaleString("de-DE")}</span>
                                <span>Pendiente: ${Number(balanceValue || 0).toLocaleString("de-DE")}</span>
                              </div>
                            </td>
                            <td className="px-3 py-3 text-right">
                              {status && (
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${paymentStatusClass[status] || "bg-gray-100 text-gray-600"}`}
                                >
                                  {paymentStatusLabel[status] || "Pendiente"}
                                </span>
                              )}
                            </td>
                            {canDeleteStock && (
                              <td className="px-3 py-3 text-right">
                                <button
                                  type="button"
                                  onClick={(event) => handleDeleteStockFromList(event, stock)}
                                  disabled={deletingStockId === stock.id}
                                  className="bg-red-500 text-white p-2 rounded hover:bg-red-600 disabled:opacity-60"
                                  aria-label="Borrar compra"
                                  title="Borrar compra"
                                >
                                  {deletingStockId === stock.id ? (
                                    <span className="text-xs">...</span>
                                  ) : (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      strokeWidth="1.5"
                                      stroke="currentColor"
                                      className="w-4 h-4"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.166L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.021-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                      />
                                    </svg>
                                  )}
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* Expenses Tab */}
        {activeTab === "gastos" && (
          <div className="grid grid-cols-1 gap-4">
            {expenses.length === 0 ? (
              <p className="text-center text-gray-500">No hay gastos registrados.</p>
            ) : (
              expenses.map((expense) => (
                <div
                  key={expense.id}
                  className="p-5 bg-white rounded-lg shadow cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => setSelectedItem(expense)}
                >
                  <h3 className="text-lg font-semibold">{expense.title}</h3>
                  <p className="text-gray-600">{expense.description}</p>
                  <p className="text-gray-800 font-bold">Monto: ${expense.amount}</p>
                </div>
              ))
            )}
          </div>
        )}

        {/* Detail Modal */}
        {selectedItem && (
          <div
            className="fixed inset-0 bg-slate-900/60 flex justify-center items-start z-50 px-4 py-6 overflow-y-auto"
            onClick={handleCloseModal}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl border border-slate-200 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 bg-slate-50 border-b border-slate-200 px-6 py-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {activeTab === "productos" ? "Detalle de compra" : "Detalle de gasto"}
                  </p>
                  <h2 className="text-xl font-semibold text-slate-900">
                    {activeTab === "productos"
                      ? selectedItem.varietyName || "Producto"
                      : selectedItem.title}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="text-slate-500 hover:text-slate-900"
                  aria-label="Cerrar"
                >
                  ✕
                </button>
              </div>

              <div className="px-6 py-5 space-y-5 max-h-[82vh] overflow-y-auto">
                {activeTab === "productos" ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] gap-4">
                      <img
                        src={selectedItem.varietyImage || "https://placehold.co/320x240"}
                        alt={selectedItem.varietyName || "Producto"}
                        className="w-full h-48 md:h-full object-cover rounded-xl border border-slate-200"
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Proveedor</p>
                          {isEditingStock ? (
                            <div className="mt-1 relative" ref={editProviderSelectRef}>
                              <button
                                type="button"
                                onClick={() => {
                                  setIsEditProviderSelectOpen((prev) => !prev);
                                  if (!providersLoaded) loadEditProviders();
                                }}
                                className="border border-gray-300 rounded-lg p-2 w-full text-left bg-white flex items-center justify-between text-sm"
                              >
                                <span className={selectedEditProvider ? "text-gray-900" : "text-gray-500"}>
                                  {selectedEditProvider?.name || selectedItem.Provider?.name || "Selecciona un proveedor"}
                                </span>
                                <span className="text-gray-500">▾</span>
                              </button>
                              {isEditProviderSelectOpen && (
                                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg">
                                  <div className="p-2 border-b border-gray-100">
                                    <input
                                      type="text"
                                      placeholder="Buscar proveedor..."
                                      value={editProviderSearchTerm}
                                      onChange={(event) => setEditProviderSearchTerm(event.target.value)}
                                      className="border border-gray-300 rounded p-2 w-full text-sm"
                                    />
                                  </div>
                                  <ul className="max-h-60 overflow-y-auto" role="listbox">
                                    {providersLoading ? (
                                      <li className="px-3 py-2 text-sm text-gray-500">Cargando proveedores...</li>
                                    ) : providersError ? (
                                      <li className="px-3 py-2 text-sm text-red-600">{providersError}</li>
                                    ) : filteredEditProviders.length === 0 ? (
                                      <li className="px-3 py-2 text-sm text-gray-500">
                                        {editProviderSearchTerm ? "Sin resultados" : "No hay proveedores"}
                                      </li>
                                    ) : (
                                      filteredEditProviders.map((provider) => {
                                        const isSelected = String(provider.id) === selectedEditProviderId;
                                        return (
                                          <li key={provider.id}>
                                            <button
                                              type="button"
                                              onClick={() => handleSelectEditProvider(provider.id)}
                                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                                                isSelected ? "bg-gray-100 font-medium" : ""
                                              }`}
                                              role="option"
                                              aria-selected={isSelected}
                                            >
                                              <div className="font-medium text-gray-900">{provider.name}</div>
                                              {provider.rut && (
                                                <div className="text-xs text-gray-500">{provider.rut}</div>
                                              )}
                                            </button>
                                          </li>
                                        );
                                      })
                                    )}
                                  </ul>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm font-semibold text-gray-900">{selectedItem.Provider?.name || "N/A"}</p>
                          )}
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">{isEditingStock ? "Estado proyectado" : "Estado"}</p>
                          <span
                            className={`inline-flex mt-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                              paymentStatusClass[selectedSummary?.paymentStatus || selectedItem.paymentStatus] || "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {paymentStatusLabel[selectedSummary?.paymentStatus || selectedItem.paymentStatus] || "Pendiente"}
                          </span>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Cantidad</p>
                          {isEditingStock ? (
                            <input
                              type="number"
                              inputMode="decimal"
                              min="0"
                              step="0.001"
                              value={stockEditForm.quantity}
                              onChange={(event) => handleStockEditChange("quantity", event.target.value)}
                              className="mt-1 border border-gray-300 rounded-lg p-2 w-full text-sm bg-white"
                            />
                          ) : (
                            <p className="text-sm font-semibold text-gray-900">{formatSignificantNumber(selectedItem.quantity)}</p>
                          )}
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Unidad</p>
                          {isEditingStock ? (
                            <select
                              value={stockEditForm.metricUnit}
                              onChange={(event) => handleStockEditMetricUnitChange(event.target.value)}
                              className="mt-1 border border-gray-300 rounded-lg p-2 w-full text-sm bg-white"
                            >
                              <option value="">Selecciona una unidad</option>
                              {stockMetricUnitOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <p className="text-sm font-semibold text-gray-900">{selectedItem.metricUnit || "N/A"}</p>
                          )}
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Precio unitario</p>
                          {isEditingStock ? (
                            <input
                              type="number"
                              inputMode="numeric"
                              min="0"
                              step="1"
                              value={stockEditForm.price}
                              onChange={(event) => handleStockEditChange("price", event.target.value)}
                              className="mt-1 border border-gray-300 rounded-lg p-2 w-full text-sm bg-white"
                            />
                          ) : (
                            <p className="text-sm font-semibold text-gray-900">
                              ${Number(selectedItem.price || 0).toLocaleString("de-DE")}
                            </p>
                          )}
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Subtotal</p>
                          <p className="text-sm font-semibold text-gray-900">
                            ${Number(selectedSummary?.totalAmount || 0).toLocaleString("de-DE")}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Pagado</p>
                          <p className="text-sm font-semibold text-gray-900">
                            ${Number(selectedSummary?.paidAmount || 0).toLocaleString("de-DE")}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Pendiente</p>
                          <p className="text-sm font-semibold text-gray-900">
                            ${Number(selectedSummary?.balance || 0).toLocaleString("de-DE")}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Método de pago</p>
                          {isEditingStock ? (
                            <select
                              value={stockEditForm.paymentMethod}
                              onChange={(event) => handleStockEditChange("paymentMethod", event.target.value)}
                              className="mt-1 border border-gray-300 rounded-lg p-2 w-full text-sm bg-white"
                            >
                              <option value="">Selecciona un método</option>
                              {paymentMethods.map((method) => (
                                <option key={method.id} value={method.id}>
                                  {method.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <p className="text-sm font-semibold text-gray-900">
                              {paymentMethods.find((method) => method.id === selectedItem.paymentMethod)?.label || selectedItem.paymentMethod || "N/A"}
                            </p>
                          )}
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p className="text-xs text-gray-500">Peso</p>
                          {isEditingStock ? (
                            <input
                              type="number"
                              inputMode="decimal"
                              min="0"
                              step="0.001"
                              value={stockEditForm.weight}
                              onChange={(event) => handleStockEditChange("weight", event.target.value)}
                              className="mt-1 border border-gray-300 rounded-lg p-2 w-full text-sm bg-white"
                              placeholder="Kg"
                            />
                          ) : (
                            <p className="text-sm font-semibold text-gray-900">{selectedItem.weight || "N/A"} kg</p>
                          )}
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 sm:col-span-2">
                          <p className="text-xs text-gray-500">Bultos por bin</p>
                          {isEditingStock ? (
                            isBinUnit(stockEditForm.metricUnit) ? (
                              <input
                                type="number"
                                inputMode="numeric"
                                min="1"
                                step="1"
                                value={stockEditForm.bultosPerBin}
                                onChange={(event) => handleStockEditChange("bultosPerBin", event.target.value)}
                                className="mt-1 border border-gray-300 rounded-lg p-2 w-full text-sm bg-white"
                              />
                            ) : (
                              <p className="text-sm text-gray-500 mt-1">
                                Solo aplica cuando la unidad es Bin.
                              </p>
                            )
                          ) : (
                            <p className="text-sm font-semibold text-gray-900">
                              {isBinUnit(selectedItem.metricUnit) ? (selectedItem.bultosPerBin || "N/A") : "No aplica"}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    {isEditingStock && stockEditError && (
                      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {stockEditError}
                      </div>
                    )}

                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                      <h3 className="text-sm font-semibold text-emerald-900">Aporte al estimado mayorista general</h3>
                      {selectedEstimateMeta?.considered ? (
                        <div className="mt-2 space-y-1 text-sm text-emerald-900">
                          <p>
                            <span className="font-semibold">Precio usado:</span>{" "}
                            ${Number(selectedEstimateMeta.unitPrice || 0).toLocaleString("de-DE")}
                            {selectedEstimateMeta.metricUnit ? ` / ${selectedEstimateMeta.metricUnit}` : ""}
                          </p>
                          {selectedEstimateMeta.usedEquivalence && selectedEstimateMeta?.equivalence && (
                            <p>
                              <span className="font-semibold">Equivalencia:</span>{" "}
                              {formatSignificantNumber(selectedEstimateMeta.equivalence.sourceAmount || 0)} {selectedEstimateMeta.equivalence.sourceUnit} = {formatSignificantNumber(selectedEstimateMeta.equivalence.targetAmount || 0)} {selectedEstimateMeta.equivalence.targetUnit}
                            </p>
                          )}
                          <p>
                            <span className="font-semibold">Origen:</span>{" "}
                            {selectedEstimateSourceLabel}
                            {selectedEstimateMeta.wholesaleMetricUnit ? ` (${selectedEstimateMeta.wholesaleMetricUnit})` : ""}
                          </p>
                          <p>
                            <span className="font-semibold">Estimado item:</span>{" "}
                            ${selectedEstimateValue.toLocaleString("de-DE")}
                          </p>
                          <p>
                            <span className="font-semibold">Participación en total estimado:</span>{" "}
                            {selectedEstimateShare.toLocaleString("de-DE", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                            %
                          </p>
                          <p>
                            <span className="font-semibold">Total estimado general:</span>{" "}
                            ${wholesaleEstimate.toLocaleString("de-DE")}
                          </p>
                          {selectedNeedsEstimateReview ? (
                            <p className="text-amber-800 font-semibold">
                              Este item supera {ESTIMATE_WARNING_THRESHOLD}% del estimado. Confírmalo o crea equivalencia.
                            </p>
                          ) : selectedEstimateConfirmed ? (
                            <p className="text-emerald-800 font-semibold">
                              Item confirmado para esta lista.
                            </p>
                          ) : null}
                        </div>
                      ) : (
                        <div className="mt-2 text-sm text-emerald-900">
                          <p className="font-semibold">No considerado en el estimado general.</p>
                          <p className="text-emerald-800">
                            {selectedEstimateMeta?.reason || "Sin precio mayorista disponible para esta compra."}
                          </p>
                        </div>
                      )}
                      {canUpdateStock && !isEditingStock && selectedEstimateMeta?.considered && (selectedNeedsEstimateReview || selectedEstimateConfirmed || selectedEstimateMeta.usedEquivalence) && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {selectedEstimateConfirmed ? (
                            <button
                              type="button"
                              onClick={handleUnconfirmSelectedEstimate}
                              disabled={isSavingEstimateConfirmation}
                              className="px-3 py-2 rounded-lg text-xs font-semibold bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-60"
                            >
                              {isSavingEstimateConfirmation ? "Guardando..." : "Quitar confirmación"}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={handleConfirmSelectedEstimate}
                              disabled={isSavingEstimateConfirmation}
                              className="px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                            >
                              {isSavingEstimateConfirmation ? "Guardando..." : "Confirmar para esta lista"}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={handleOpenEquivalenceModal}
                            disabled={isSavingEquivalence || selectedStockEquivalenceOptions.length === 0}
                            className="px-3 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                          >
                            Crear equivalencia
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-gray-900">Pagos registrados</h3>
                      {selectedItem.StockPayments && selectedItem.StockPayments.length > 0 ? (
                        <div className="space-y-2">
                          {selectedItem.StockPayments.map((payment) => (
                            <div
                              key={payment.id}
                              className="border border-slate-200 rounded-xl p-3 bg-white text-sm"
                            >
                              <p>
                                <span className="font-semibold">Monto:</span>{" "}
                                ${Number(payment.amount || 0).toLocaleString("de-DE")}
                              </p>
                              <p>
                                <span className="font-semibold">Metodo:</span>{" "}
                                {payment.method}
                                {(payment.source === "general" || payment.providerPaymentId) && (
                                  <span className="ml-2 text-xs font-semibold px-2 py-1 rounded bg-blue-100 text-blue-700">
                                    Pago general
                                  </span>
                                )}
                              </p>
                              {payment.reference && (
                                <p>
                                  <span className="font-semibold">Referencia:</span>{" "}
                                  {payment.reference}
                                </p>
                              )}
                              {payment.voucherUrl && (
                                <p>
                                  <span className="font-semibold">Comprobante:</span>{" "}
                                  <a
                                    href={payment.voucherUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-600 underline"
                                  >
                                    Ver
                                  </a>
                                </p>
                              )}
                              {payment.paidAt && (
                                <p>
                                  <span className="font-semibold">Fecha:</span>{" "}
                                  {new Date(payment.paidAt).toLocaleDateString("es-CL")}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-600 space-y-1 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                          <p>Sin pagos registrados.</p>
                          {selectedItem.paymentMethod && (
                            <p>Metodo registrado: {selectedItem.paymentMethod}</p>
                          )}
                        </div>
                      )}
                    </div>

                    {canUpdateStock && Number(selectedSummary?.balance || 0) > 0 && (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                        <h3 className="text-sm font-semibold text-gray-900">Agregar pago</h3>
                        {paymentError && (
                          <p className="text-sm text-red-600">{paymentError}</p>
                        )}
                        {isEditingStock && (
                          <p className="text-xs text-amber-700">
                            Termina la edición de la compra para registrar nuevos pagos.
                          </p>
                        )}
                        <div className="text-xs text-gray-600">
                          Pendiente actual: ${selectedBalance.toLocaleString("de-DE")} | Restante despues de este pago: ${remainingAfter.toLocaleString("de-DE")}
                        </div>
                        {overpayAmount > 0 && (
                          <p className="text-xs text-red-600">
                            Estas pagando de mas por ${overpayAmount.toLocaleString("de-DE")}.
                          </p>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <input
                            type="number"
                            placeholder="Monto"
                            value={paymentForm.amount}
                            onChange={(e) => handlePaymentChange("amount", e.target.value)}
                            className="border border-gray-300 rounded-lg p-2 w-full"
                            disabled={isEditingStock || isSavingPayment}
                          />
                          <select
                            value={paymentForm.method}
                            onChange={(e) => handlePaymentChange("method", e.target.value)}
                            className="border border-gray-300 rounded-lg p-2 w-full"
                            disabled={isEditingStock || isSavingPayment}
                          >
                            <option value="">Metodo</option>
                            {paymentMethods.map((method) => (
                              <option key={method.id} value={method.id}>
                                {method.label}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            placeholder="Referencia"
                            value={paymentForm.reference}
                            onChange={(e) => handlePaymentChange("reference", e.target.value)}
                            className="border border-gray-300 rounded-lg p-2 w-full"
                            disabled={isEditingStock || isSavingPayment}
                          />
                          <input
                            type="date"
                            value={paymentForm.paidAt}
                            onChange={(e) => handlePaymentChange("paidAt", e.target.value)}
                            className="border border-gray-300 rounded-lg p-2 w-full"
                            disabled={isEditingStock || isSavingPayment}
                          />
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Comprobante (opcional)
                            </label>
                            <input
                              type="file"
                              accept="image/*,application/pdf"
                              onChange={(e) =>
                                handlePaymentVoucherChange(e.target.files?.[0] || null)
                              }
                              className="border border-gray-300 rounded-lg p-2 w-full"
                              disabled={isEditingStock || isSavingPayment}
                            />
                            {paymentForm.voucherFile?.name && (
                              <p className="text-xs text-gray-500 mt-1">
                                {paymentForm.voucherFile.name}
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={handleAddPayment}
                          disabled={isSavingPayment || isEditingStock}
                          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                        >
                          {isSavingPayment ? <InlineLoading label="Guardando" /> : "Registrar pago"}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-2 text-sm text-gray-700">
                    <p><span className="font-semibold">Descripción:</span> {selectedItem.description}</p>
                    <p><span className="font-semibold">Monto:</span> ${selectedItem.amount}</p>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row gap-2 sm:justify-end">
                {activeTab === "productos" && isEditingStock && canUpdateStock && (
                  <>
                    <button
                      onClick={handleCancelEditingStock}
                      disabled={isSavingStockEdit}
                      className="w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-60"
                    >
                      Cancelar edición
                    </button>
                    <button
                      onClick={handleSaveStockEdit}
                      disabled={isSavingStockEdit}
                      className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                    >
                      {isSavingStockEdit ? "Guardando..." : "Guardar cambios"}
                    </button>
                  </>
                )}
                {activeTab === "productos" && !isEditingStock && canUpdateStock && (
                  <button
                    onClick={handleStartEditingStock}
                    className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Editar compra
                  </button>
                )}
                {activeTab === "productos" && !isEditingStock && canDeleteStock && (
                  <button
                    onClick={handleDeleteStockItem}
                    disabled={isDeletingStock}
                    className="w-full sm:w-auto px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-60 flex items-center justify-center"
                    aria-label="Borrar compra"
                    title="Borrar compra"
                  >
                    {isDeletingStock ? (
                      <span className="text-sm">...</span>
                    ) : (
                      "Eliminar compra"
                    )}
                  </button>
                )}
                <button
                  onClick={handleCloseModal}
                  className="w-full sm:w-auto px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
        {equivalenceModal.isOpen && (
          <div
            className="fixed inset-0 bg-black/50 flex items-start justify-center z-[60] px-4 py-8 overflow-y-auto"
            onClick={handleCloseEquivalenceModal}
          >
            <div
              className="w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Estimado mayorista</p>
                  <h3 className="text-base font-semibold text-slate-900">Crear equivalencia global</h3>
                </div>
                <button
                  type="button"
                  onClick={handleCloseEquivalenceModal}
                  className="text-slate-500 hover:text-slate-900"
                  aria-label="Cerrar"
                >
                  ✕
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                  <p className="text-xs text-gray-500">Unidad origen</p>
                  <p className="font-semibold text-gray-900">{equivalenceModal.fromUnit || "N/A"}</p>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">x_amount</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.001"
                      placeholder="Ej: 1"
                      value={equivalenceModal.fromAmount}
                      onChange={(event) => setEquivalenceModal((prev) => ({
                        ...prev,
                        fromAmount: event.target.value,
                        error: "",
                      }))}
                      className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">from_metric</label>
                    <div className="w-full border border-gray-300 rounded-lg p-2 text-sm bg-gray-50 text-gray-800">
                      {equivalenceModal.fromUnit || "N/A"}
                    </div>
                  </div>
                  <p className="text-center text-sm font-semibold text-gray-600">=</p>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">y_amount</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.001"
                      placeholder="Ej: 18"
                      value={equivalenceModal.toAmount}
                      onChange={(event) => setEquivalenceModal((prev) => ({
                        ...prev,
                        toAmount: event.target.value,
                        error: "",
                      }))}
                      className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">to_metric</label>
                    <select
                      value={equivalenceModal.toUnit}
                      onChange={(event) => setEquivalenceModal((prev) => ({
                        ...prev,
                        toUnit: event.target.value,
                        error: "",
                      }))}
                      className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                    >
                      {selectedStockEquivalenceOptions.map((option) => (
                        <option key={option.key} value={option.label}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-[11px] text-gray-500 -mt-1">
                  {modalSourceAmountLabel} {equivalenceModal.fromUnit || "origen"} = {modalTargetAmountLabel} {equivalenceModal.toUnit || "destino"}
                </p>
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 space-y-1">
                  <p className="font-semibold">Como se usa esta equivalencia</p>
                  <p>
                    Se guarda para ambos sentidos.
                  </p>
                  <p>
                    1 {equivalenceModal.fromUnit || "origen"} = {modalForwardRatioLabel} {equivalenceModal.toUnit || "destino"}
                  </p>
                  <p>
                    1 {equivalenceModal.toUnit || "destino"} = {modalReverseRatioLabel} {equivalenceModal.fromUnit || "origen"}
                  </p>
                  <p className="text-[11px] text-blue-800">
                    Formula aplicada al estimado: precio por {equivalenceModal.fromUnit || "origen"} = precio por {equivalenceModal.toUnit || "destino"} x ({modalTargetAmountLabel} / {modalSourceAmountLabel})
                  </p>
                </div>
                {equivalenceModal.error && (
                  <p className="text-xs text-red-600">{equivalenceModal.error}</p>
                )}
                {selectedEquivalencesForSource.length > 0 && (
                  <div className="rounded-lg border border-gray-200 p-3 space-y-2">
                    <p className="text-xs font-semibold text-gray-600 uppercase">Equivalencias actuales para esta unidad</p>
                    {selectedEquivalencesForSource.map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-gray-800">
                          {formatSignificantNumber(entry.sourceAmount || 0)} {entry.sourceUnit} = {formatSignificantNumber(entry.targetAmount || 0)} {entry.targetUnit}
                        </span>
                        {canUpdateStock && (
                          <button
                            type="button"
                            onClick={() => handleDeleteEquivalence(entry.id)}
                            disabled={deletingEquivalenceId === entry.id}
                            className="px-2 py-1 text-xs rounded bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-60"
                          >
                            {deletingEquivalenceId === entry.id ? "..." : "Eliminar"}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="px-5 py-4 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row gap-2 sm:justify-end">
                <button
                  type="button"
                  onClick={handleCloseEquivalenceModal}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSaveEquivalence}
                  disabled={isSavingEquivalence}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {isSavingEquivalence ? "Guardando..." : "Guardar equivalencia"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockListViewPage;
